/**
 * ResponsiveTabs — App-level shim wrapping DS tab patterns with responsive overflow.
 *
 * Behavior:
 * - Desktop (>900px): shows visible tabs + "More" dropdown for overflow
 * - Mobile (≤900px): collapses to a select-style dropdown
 *
 * Variants:
 * - `primary` (default): pill tab bar on page background above content (section level)
 * - `secondary`: underline text tabs nested inside a white content card (subsection)
 *
 * This shim exists because the DS Tabs component handles tab-bar rendering
 * only. The responsive overflow + content wrapper pattern is app-level
 * behavior that belongs in the shim layer (not in the DS).
 *
 * Replaces: StaffologyTabSystem
 */
import {ChevronDown} from 'lucide-react'
import React, {useState, useRef, useEffect, useCallback} from 'react'
import styled, {css} from 'styled-components'

export interface ResponsiveTab {
  id: string
  label: string
  disabled?: boolean
}

export type ResponsiveTabsVariant = 'primary' | 'secondary'

export interface ResponsiveTabsProps {
  tabs: ResponsiveTab[]
  activeTab?: string
  onTabChange?: (tabId: string) => void
  children: React.ReactNode
  /**
   * `primary` — section-level pill tabs above content (default, unchanged).
   * `secondary` — in-card underline tabs nested under a primary selection.
   */
  variant?: ResponsiveTabsVariant
  /** data-testid for the tab container */
  'data-testid'?: string
}

/* ── Styled components ───────────────────────────────────── */

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
`

const SecondaryCard = styled.div`
  background: var(--white);
  border: var(--border-thin) solid var(--border-subtle);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-mid-strong);
  overflow: hidden;
`

const TabBar = styled.div<{$variant: ResponsiveTabsVariant}>`
  display: flex;
  position: relative;

  ${({$variant}) =>
    $variant === 'primary'
      ? css`
          gap: 2px;
          background: var(--neutral-100);
          border-radius: var(--radius-sm);
          padding: 2px;
          margin-bottom: var(--space-4);
        `
      : css`
          gap: var(--space-2);
          background: var(--white);
          padding: 0 var(--space-6);
          border-bottom: var(--border-thin) solid var(--border-subtle);
          margin-bottom: 0;
        `}

  @media (max-width: 900px) {
    display: none;
  }
`

const TabButton = styled.button<{
  $isActive: boolean
  $variant: ResponsiveTabsVariant
  disabled?: boolean
}>`
  border: none;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  position: relative;
  white-space: nowrap;
  font-family: var(--font-sans);
  letter-spacing: var(--ls-normal);
  transition: color var(--motion-fast) var(--motion-ease),
    background var(--motion-fast) var(--motion-ease),
    box-shadow var(--motion-fast) var(--motion-ease),
    border-color var(--motion-fast) var(--motion-ease);

  ${({$variant, $isActive, disabled}) =>
    $variant === 'primary'
      ? css`
          padding: var(--space-3) var(--space-5);
          background: ${$isActive ? 'var(--white)' : 'transparent'};
          color: ${disabled
            ? 'var(--text-disabled)'
            : $isActive
            ? 'var(--brand-cta)'
            : 'var(--text-secondary)'};
          font-weight: ${$isActive ? 'var(--fw-semibold)' : 'var(--fw-medium)'};
          font-size: var(--fs-body);
          border-radius: calc(var(--radius-sm) - 2px);
          ${$isActive &&
          css`
            box-shadow: var(--shadow-mid-strong);
          `}

          &:hover:not(:disabled) {
            background: ${$isActive ? 'var(--white)' : 'var(--neutral-50)'};
            color: var(--brand-cta);
          }
        `
      : css`
          padding: var(--space-3) var(--space-1);
          background: transparent;
          color: ${disabled
            ? 'var(--text-disabled)'
            : $isActive
            ? 'var(--brand-cta)'
            : 'var(--text-secondary)'};
          font-weight: var(--fw-regular);
          font-size: var(--fs-caption);
          border-radius: 0;
          border-bottom: var(--border-thick) solid ${$isActive ? 'var(--brand-cta)' : 'transparent'};
          margin-bottom: calc(var(--border-thin) * -1);

          &:hover:not(:disabled) {
            color: ${$isActive ? 'var(--brand-cta)' : 'var(--text-primary)'};
          }
        `}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

/* ── More dropdown (desktop overflow) ─────────────────────── */

const MoreDropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const MoreButton = styled.button<{
  $isActive: boolean
  $isOpen: boolean
  $variant: ResponsiveTabsVariant
}>`
  border: none;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  white-space: nowrap;
  font-family: var(--font-sans);
  letter-spacing: var(--ls-normal);
  transition: color var(--motion-fast) var(--motion-ease),
    background var(--motion-fast) var(--motion-ease),
    box-shadow var(--motion-fast) var(--motion-ease);

  ${({$variant, $isActive, $isOpen}) =>
    $variant === 'primary'
      ? css`
          padding: var(--space-3) var(--space-5);
          background: ${$isActive || $isOpen ? 'var(--white)' : 'transparent'};
          color: ${$isActive || $isOpen ? 'var(--brand-cta)' : 'var(--text-secondary)'};
          font-weight: ${$isActive ? 'var(--fw-semibold)' : 'var(--fw-medium)'};
          font-size: var(--fs-body);
          border-radius: calc(var(--radius-sm) - 2px);
          ${($isActive || $isOpen) &&
          css`
            box-shadow: var(--shadow-mid-strong);
          `}

          &:hover {
            background: ${$isActive || $isOpen ? 'var(--white)' : 'var(--neutral-50)'};
            color: var(--brand-cta);
          }
        `
      : css`
          padding: var(--space-3) var(--space-1);
          background: transparent;
          color: ${$isActive || $isOpen ? 'var(--brand-cta)' : 'var(--text-secondary)'};
          font-weight: var(--fw-regular);
          font-size: var(--fs-caption);
          border-bottom: var(--border-thick) solid
            ${$isActive || $isOpen ? 'var(--brand-cta)' : 'transparent'};
          margin-bottom: calc(var(--border-thin) * -1);

          &:hover {
            color: var(--brand-cta);
          }
        `}
`

const MoreDropdownMenu = styled.div<{$isOpen: boolean}>`
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  position: absolute;
  top: calc(100% + var(--space-1));
  right: 0;
  background: var(--white);
  border: var(--border-thin) solid var(--border-subtle);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-elevated);
  z-index: var(--z-dropdown);
  min-width: 180px;
  padding: var(--space-2) 0;
`

const MoreDropdownItem = styled.button<{$isActive: boolean; disabled?: boolean}>`
  width: 100%;
  padding: var(--space-2) var(--space-4);
  background: ${(props) => (props.$isActive ? 'var(--brand-100)' : 'var(--white)')};
  border: none;
  text-align: left;
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  font-weight: ${(props) => (props.$isActive ? 'var(--fw-semibold)' : 'var(--fw-regular)')};
  color: ${(props) =>
    props.disabled
      ? 'var(--text-disabled)'
      : props.$isActive
      ? 'var(--brand-cta)'
      : 'var(--text-primary)'};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background var(--motion-fast) var(--motion-ease);

  &:hover:not(:disabled) {
    background: var(--neutral-50);
  }

  &:disabled {
    opacity: 0.4;
  }
`

/* ── Mobile dropdown ──────────────────────────────────────── */

const MobileTabDropdown = styled.div<{$variant: ResponsiveTabsVariant}>`
  display: none;

  @media (max-width: 900px) {
    display: block;
    margin-bottom: ${({$variant}) => ($variant === 'primary' ? 'var(--space-4)' : '0')};
    ${({$variant}) =>
      $variant === 'secondary' &&
      css`
        padding: var(--space-3) var(--space-6);
        border-bottom: var(--border-thin) solid var(--border-subtle);
      `}
  }
`

const MobileSelectButton = styled.button`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  background: var(--white);
  border: var(--border-thin) solid var(--border-subtle);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: var(--font-sans);
  font-size: var(--fs-subtitle);
  font-weight: var(--fw-semibold);
  color: var(--brand-cta);
  cursor: pointer;
  transition: background var(--motion-fast) var(--motion-ease),
    border-color var(--motion-fast) var(--motion-ease);

  &:hover {
    background: var(--neutral-50);
    border-color: var(--brand-cta);
  }
`

const MobileDropdownContainer = styled.div`
  position: relative;
`

const MobileDropdownMenu = styled.div<{$isOpen: boolean}>`
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--white);
  border: var(--border-thin) solid var(--border-subtle);
  border-radius: var(--radius-sm);
  margin-top: var(--space-1);
  box-shadow: var(--shadow-elevated);
  z-index: var(--z-dropdown);
  max-height: 300px;
  overflow-y: auto;
`

const MobileDropdownItem = styled.button<{$isActive: boolean; disabled?: boolean}>`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  background: ${(props) => (props.$isActive ? 'var(--brand-50)' : 'var(--white)')};
  border: none;
  border-bottom: var(--border-thin) solid var(--neutral-100);
  text-align: left;
  font-family: var(--font-sans);
  font-size: var(--fs-subtitle);
  font-weight: ${(props) => (props.$isActive ? 'var(--fw-semibold)' : 'var(--fw-regular)')};
  color: ${(props) =>
    props.disabled
      ? 'var(--text-disabled)'
      : props.$isActive
      ? 'var(--brand-cta)'
      : 'var(--text-primary)'};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background var(--motion-fast) var(--motion-ease);

  &:last-child {
    border-bottom: none;
  }

  &:hover:not(:disabled) {
    background: var(--neutral-50);
  }

  &:disabled {
    opacity: 0.6;
  }
`

const TabContent = styled.div<{$variant: ResponsiveTabsVariant}>`
  flex: 1;
  position: relative;
  min-width: 0;
  overflow: hidden;

  ${({$variant}) =>
    $variant === 'secondary' &&
    css`
      padding: var(--space-4) var(--space-6);
    `}
`

const MeasureBar = styled.div`
  display: flex;
  visibility: hidden;
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  gap: 2px;
  z-index: -1;
`

/* ── Component ────────────────────────────────────────────── */

export const ResponsiveTabs: React.FC<ResponsiveTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  children,
  variant = 'primary',
  'data-testid': testId,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(tabs[0]?.id || '')
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false)
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false)
  const [visibleTabs, setVisibleTabs] = useState<ResponsiveTab[]>(tabs)
  const [overflowTabs, setOverflowTabs] = useState<ResponsiveTab[]>([])

  const tabBarRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const moreRef = useRef<HTMLDivElement>(null)
  const mobileRef = useRef<HTMLDivElement>(null)

  const currentTab = activeTab ?? internalActiveTab

  const handleTabChange = useCallback(
    (tabId: string) => {
      if (onTabChange) {
        onTabChange(tabId)
      } else {
        setInternalActiveTab(tabId)
      }
      setMoreDropdownOpen(false)
      setMobileDropdownOpen(false)
    },
    [onTabChange],
  )

  /* Measure which tabs fit in the container width */
  useEffect(() => {
    const measureOverflow = () => {
      if (!measureRef.current || !tabBarRef.current) return

      const containerWidth = tabBarRef.current.offsetWidth
      const moreButtonWidth = 120
      const gap = variant === 'primary' ? 2 : 8
      const measureChildren = Array.from(measureRef.current.children) as HTMLElement[]

      let usedWidth = 0
      let splitIndex = tabs.length

      for (let i = 0; i < measureChildren.length; i++) {
        const childWidth = measureChildren[i]!.offsetWidth + gap
        const remaining = containerWidth - usedWidth

        if (remaining < childWidth + (i < tabs.length - 1 ? moreButtonWidth : 0)) {
          splitIndex = i
          break
        }
        usedWidth += childWidth
      }

      if (splitIndex < tabs.length) {
        setVisibleTabs(tabs.slice(0, splitIndex))
        setOverflowTabs(tabs.slice(splitIndex))
      } else {
        setVisibleTabs(tabs)
        setOverflowTabs([])
      }
    }

    measureOverflow()
    window.addEventListener('resize', measureOverflow)
    return () => window.removeEventListener('resize', measureOverflow)
  }, [tabs, variant])

  /* Close dropdowns on outside click */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setMoreDropdownOpen(false)
      }
      if (mobileRef.current && !mobileRef.current.contains(event.target as Node)) {
        setMobileDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const activeTabLabel = tabs.find((t) => t.id === currentTab)?.label || 'Select tab'
  const isActiveInOverflow = overflowTabs.some((t) => t.id === currentTab)

  const tabChrome = (
    <>
      <MeasureBar ref={measureRef}>
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            $isActive={false}
            $variant={variant}
            disabled={tab.disabled}
            style={{pointerEvents: 'none'}}>
            {tab.label}
          </TabButton>
        ))}
      </MeasureBar>

      <TabBar ref={tabBarRef} $variant={variant}>
        {visibleTabs.map((tab) => (
          <TabButton
            key={tab.id}
            $isActive={currentTab === tab.id}
            $variant={variant}
            disabled={tab.disabled}
            onClick={() => !tab.disabled && handleTabChange(tab.id)}
            data-testid={testId ? `${testId}-tab-${tab.id}` : undefined}
            role="tab"
            aria-selected={currentTab === tab.id}>
            {tab.label}
          </TabButton>
        ))}

        {overflowTabs.length > 0 && (
          <MoreDropdownContainer ref={moreRef}>
            <MoreButton
              $isActive={isActiveInOverflow}
              $isOpen={moreDropdownOpen}
              $variant={variant}
              onClick={() => setMoreDropdownOpen(!moreDropdownOpen)}
              aria-expanded={moreDropdownOpen}
              aria-haspopup="true">
              {isActiveInOverflow ? tabs.find((t) => t.id === currentTab)?.label : 'More'}
              <ChevronDown size={16} />
            </MoreButton>
            <MoreDropdownMenu $isOpen={moreDropdownOpen}>
              {overflowTabs.map((tab) => (
                <MoreDropdownItem
                  key={tab.id}
                  $isActive={currentTab === tab.id}
                  disabled={tab.disabled}
                  onClick={() => !tab.disabled && handleTabChange(tab.id)}>
                  {tab.label}
                </MoreDropdownItem>
              ))}
            </MoreDropdownMenu>
          </MoreDropdownContainer>
        )}
      </TabBar>

      <MobileTabDropdown $variant={variant}>
        <MobileDropdownContainer ref={mobileRef}>
          <MobileSelectButton
            onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
            aria-expanded={mobileDropdownOpen}
            aria-haspopup="listbox">
            {activeTabLabel}
            <ChevronDown size={16} />
          </MobileSelectButton>
          <MobileDropdownMenu $isOpen={mobileDropdownOpen}>
            {tabs.map((tab) => (
              <MobileDropdownItem
                key={tab.id}
                $isActive={currentTab === tab.id}
                disabled={tab.disabled}
                onClick={() => !tab.disabled && handleTabChange(tab.id)}
                role="option"
                aria-selected={currentTab === tab.id}>
                {tab.label}
              </MobileDropdownItem>
            ))}
          </MobileDropdownMenu>
        </MobileDropdownContainer>
      </MobileTabDropdown>
    </>
  )

  const body = <TabContent $variant={variant}>{children}</TabContent>

  return (
    <ContentContainer data-testid={testId}>
      {variant === 'secondary' ? (
        <SecondaryCard>
          {tabChrome}
          {body}
        </SecondaryCard>
      ) : (
        <>
          {tabChrome}
          {body}
        </>
      )}
    </ContentContainer>
  )
}
